import {ChevronRightIcon} from '@primer/octicons-react'
import {Link} from '@primer/react'
import {useSlots} from '@primer/react/experimental'
import type {SoftNavOnlyLink as RouterLink} from '@github-ui/react-router'
import Title from './Title'
import Description from './Description'
import itemStyles from './Item.module.css'
import {clsx} from 'clsx'
import styles from './LinkItem.module.css'

const slotConfig = {
  title: Title,
  description: Description,
}

/**
 * Every prop supported by the router `Link` (`to`, `replace`, `state`,
 * `preventScrollReset`, `reloadDocument`, `preload`, `preloadDelay`, `ref`, …),
 * derived from the component itself so the contract stays in sync. `children`
 * and `className` are owned by `LinkItem`, so they are not forwardable.
 */
export type RouterLinkProps = Omit<React.ComponentProps<typeof RouterLink>, 'children' | 'className'> &
  Record<`data-${string}`, string>

type BaseLinkProps = {
  children: React.ReactNode
  leadingIcon?: React.ReactNode
  nestedLevel?: 0 | 1 | 2
  value?: string
}

type NativeLinkProps = BaseLinkProps & {
  href: string
  as?: never
  routerLinkProps?: never
}

type RouterLinkItemProps = BaseLinkProps & {
  as: typeof RouterLink
  routerLinkProps: RouterLinkProps
  href?: never
}

export type ControlGroupLinkProps = NativeLinkProps | RouterLinkItemProps

const LinkItem = ({
  children,
  leadingIcon = null,
  href,
  value,
  nestedLevel = 0,
  as,
  routerLinkProps,
}: ControlGroupLinkProps) => {
  const [slots] = useSlots(children, slotConfig)
  const {title, description} = slots

  return (
    <div className={clsx(itemStyles.container, styles.container)} style={{'--nested-level': nestedLevel}}>
      <div className={itemStyles.contents}>
        {leadingIcon ? (
          <div className={itemStyles.leadingVisual}>
            <div className={styles.leadingIcon}>{leadingIcon}</div>
          </div>
        ) : null}

        <Link
          {...routerLinkProps}
          as={as as React.ElementType | undefined}
          href={href}
          className={clsx(itemStyles.title, styles.linkOverlay)}
        >
          {title}
        </Link>

        {/* eslint-disable-next-line @github-ui/github-monorepo/no-global-classname-literals */}
        <div className={clsx('descriptionBox', itemStyles.description)}>{description}</div>

        <div className={itemStyles.trailingVisual}>
          <div className={styles.linkIndicator}>
            {value && <span>{value}</span>}
            <ChevronRightIcon />
          </div>
        </div>
      </div>
    </div>
  )
}

export default LinkItem
